// import { locationsGroups } from "@utils/constants";
import React, { useEffect, useState } from "react";

import { Button, CalendarPicker, Select, Small } from "@fronterahealth/frontera-ui-components";

import { GoalStatusEnums, SortBy } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

interface FilterState {
  location?: string;
  targetStatus?: GoalStatusEnums;
  dateRange?: {
    startDate?: Date;
    endDate?: Date;
  };
  sortBy: SortBy;
}

interface ProgrammingGoalVideoMomentsFilterProps {
  setSelectedFilters: (filters: FilterState) => void;
  currentFilters: FilterState;
}

const ProgrammingGoalVideoMomentsFilter: React.FC<ProgrammingGoalVideoMomentsFilterProps> = ({
  setSelectedFilters,
  currentFilters,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(currentFilters.location);
  const [selectedTargetStatus, setSelectedTargetStatus] = useState<GoalStatusEnums | undefined>(
    currentFilters.targetStatus,
  );
  const [selectedDateRange, setSelectedDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>(currentFilters.dateRange || {});
  const [selectedSortBy, setSelectedSortBy] = useState<SortBy>(currentFilters.sortBy || SortBy.LastUpdated);

  useEffect(() => {
    setSelectedFilters({
      location: selectedLocation,
      targetStatus: selectedTargetStatus,
      dateRange: selectedDateRange,
      sortBy: selectedSortBy,
    });
  }, [selectedLocation, selectedTargetStatus, selectedDateRange, selectedSortBy, setSelectedFilters]);

  const handleClearFilters = () => {
    setSelectedLocation(undefined);
    setSelectedTargetStatus(undefined);
    setSelectedDateRange({});
    setSelectedSortBy(SortBy.LastUpdated);
  };

  const hasActiveFilters = !!(
    selectedLocation ||
    selectedTargetStatus ||
    selectedDateRange.startDate ||
    selectedDateRange.endDate
  );

  const sortByItems = Object.values(SortBy).map((sort) => ({
    id: sort,
    primary: convertReadableString(sort),
  }));

  return (
    <div className="flex flex-row bg-limestone-100 rounded-md px-4 pt-1 w-full">
      <div className="flex flex-row w-full items-center justify-start">
        <span className="flex items-center justify-start align-start gap-x-1">
          {/* TODO: add back locations filter when locations are actually being assigned to video moments */}
          {/* <Select
            title=""
            items={locationsGroups[0].map((location) => ({
              primary: location,
              id: location,
            }))}
            setSelected={(selected) => {
              setSelectedLocation(selected.id as string | undefined);
            }}
            selected={
              selectedLocation
                ? {
                    primary: selectedLocation,
                    id: selectedLocation,
                  }
                : undefined
            }
            placeholderText="Location"
            className="max-h-14! min-w-[12.5vw]! max-w-[12.5vw]!"
          /> */}
          <Select
            title=""
            items={Object.values(GoalStatusEnums).map((status) => ({
              primary: convertReadableString(status),
              id: status,
            }))}
            setSelected={(selected) => {
              setSelectedTargetStatus(selected.id as GoalStatusEnums | undefined);
            }}
            selected={
              selectedTargetStatus
                ? {
                    primary: convertReadableString(selectedTargetStatus),
                    id: selectedTargetStatus,
                  }
                : undefined
            }
            placeholderText="Target Status"
            className="max-h-14! min-w-[12.5vw]! max-w-[12.5vw]!"
          />
          <CalendarPicker
            placeholder="Date"
            selectedDateRange={selectedDateRange}
            inputSize="xs"
            onDateChange={(dateRange) => {
              setSelectedDateRange(dateRange);
            }}
            variant="default"
            containerClass="max-h-14! min-w-[12.5vw]! max-w-[16.666666666666668vw]! [&_div:nth-child(2)]:py-2 [&_div:nth-child(2)]:mb-1"
          />
        </span>
        {hasActiveFilters && (
          <Button
            text="Clear Filters"
            appearance="text"
            onClick={handleClearFilters}
            className="text-sm shrink text-hatch-500 hover:text-hatch-700 hover:underline mb-5"
          ></Button>
        )}
      </div>
      <div className="flex flex-row items-center shrink-0">
        <Small colorType="primary" displayType="loud">
          Sort by:
        </Small>
        <Select
          items={sortByItems}
          title=""
          placeholderText=""
          selected={{ id: selectedSortBy, primary: convertReadableString(selectedSortBy) }}
          setSelected={(selected) => {
            setSelectedSortBy(selected.id as SortBy);
          }}
          inputContainerClassName="mt-0 min-w-[10vw]! max-w-[12.5vw]!"
          className="last-updated-select"
        />
      </div>
    </div>
  );
};

export default ProgrammingGoalVideoMomentsFilter;
